import {
  CheckSquare,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  MessageSquarePlus,
  ListTree,
  Expand,
  Sparkles,
  SearchCheck,
  Text,
  TextQuote,
} from "lucide-react";
import { Command, SuggestionItem, renderItems } from "novel";
import {
  THESIS_ACTIONS,
  saveGenerateExtras,
  type ThesisGenerateAction,
} from "@/lib/thesisGenerateExtras";

interface suggestionOptions {
  title: string;
  children: SuggestionItem[]
}

const ACTION_ICONS: Record<ThesisGenerateAction, JSX.Element> = {
  continue: <MessageSquarePlus size={18} />,
  outline: <ListTree size={18} />,
  expand: <Expand size={18} />,
  polish: <Sparkles size={18} />,
  review: <SearchCheck size={18} />,
};

function insertAiWithAction(action: ThesisGenerateAction): SuggestionItem["command"] {
  return ({ editor, range }) => {
    saveGenerateExtras({ action });
    editor.chain().focus().deleteRange(range).setAiGeneration({ collapsed: false }).run();
  };
}

/** 论文写作斜杠菜单：AI 动作与格式同入口，选即生成 */
export const suggestionOptions: suggestionOptions[] = [
  {
    title: "AI 写作（选一项后点生成）",
    children: THESIS_ACTIONS.map((a) => ({
      title: a.label,
      description: a.hint,
      searchTerms: [a.id, a.label, "ai", "写"],
      icon: ACTION_ICONS[a.id],
      command: insertAiWithAction(a.id),
    })),
  },
  {
    title: "正文",
    children: [
      {
        title: "正文段落",
        description: "插入普通段落。",
        searchTerms: ["p", "paragraph"],
        icon: <Text size={18} />,
        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).toggleNode("paragraph", "paragraph").run();
        },
      },
    ]
  },
  {
    title: "标题",
    children: [
      {
        title: "一级标题",
        description: "大节标题。",
        searchTerms: ["h1", "title"],
        icon: <Heading1 size={18} />,
        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).setNode("heading", { level: 1 }).run();
        },
      },
      {
        title: "二级标题",
        description: "小节标题。",
        searchTerms: ["h2"],
        icon: <Heading2 size={18} />,
        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).setNode("heading", { level: 2 }).run();
        },
      },
      {
        title: "三级标题",
        description: "更细的小标题。",
        searchTerms: ["h3"],
        icon: <Heading3 size={18} />,
        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).setNode("heading", { level: 3 }).run();
        },
      },
    ]
  },
  {
    title: "列表",
    children: [
      {
        title: "要点清单",
        description: "概念、案例或待核文献要点。",
        searchTerms: ["todo", "task", "list", "check"],
        icon: <CheckSquare size={18} />,
        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).toggleTaskList().run();
        },
      },
      {
        title: "无序列表",
        description: "项目符号列表。",
        searchTerms: ["ul", "bullet"],
        icon: <List size={18} />,
        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).toggleBulletList().run();
        },
      },
      {
        title: "有序列表",
        description: "编号列表。",
        searchTerms: ["ol", "numbered"],
        icon: <ListOrdered size={18} />,
        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).toggleOrderedList().run();
        },
      },
      {
        title: "引用",
        description: "引用块。",
        searchTerms: ["quote"],
        icon: <TextQuote size={18} />,
        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).toggleBlockquote().run();
        },
      },
    ]
  },
];

export const slashCommand = Command.configure({
  suggestion: {
    items: () => suggestionOptions,
    render: renderItems,
  },
});
